import { useEffect } from 'react';
import { useLocation } from 'wouter';
import {
  SUPPORTED_SLUGS,
  slugToHreflang,
  getSlugFromPath,
  stripLocaleFromPath,
  buildLocalePath,
  DEFAULT_SLUG,
} from '@/i18n/locale-utils';

const SITE_ORIGIN = 'https://traverba.com';

// Pages are served with a trailing slash (Cloudflare 308-redirects the slashless
// form). Canonical + hreflang URLs must match that final form and the sitemap,
// otherwise the rendered DOM points canonicals at a redirecting URL.
const withSlash = (u: string) => (u.endsWith('/') ? u : u + '/');

export function HreflangMeta() {
  // Subscribe to wouter's location so canonical + hreflang recompute on SPA
  // navigation (window.location.pathname is already updated by the time this
  // re-renders). Without this the tags stayed frozen at the first-loaded URL.
  useLocation();
  const path = window.location.pathname;
  const currentSlug = getSlugFromPath(path);
  const subpath = stripLocaleFromPath(path);

  useEffect(() => {
    // Update <html lang>
    const hreflang = currentSlug ? slugToHreflang[currentSlug] : 'en';
    document.documentElement.lang = hreflang;

    // Remove existing hreflang/canonical tags — both the static one injected at
    // build time and any previously added by this component — so the rendered
    // page ends up with exactly one canonical.
    document
      .querySelectorAll('link[data-hreflang-meta], link[rel="canonical"]')
      .forEach((el) => el.remove());

    const head = document.head;

    // Canonical
    const canonical = document.createElement('link');
    canonical.rel = 'canonical';
    canonical.href = withSlash(SITE_ORIGIN + (currentSlug ? buildLocalePath(currentSlug, subpath) : '/en/'));
    canonical.setAttribute('data-hreflang-meta', '');
    head.appendChild(canonical);

    // Alternates for each locale
    for (const slug of SUPPORTED_SLUGS) {
      const link = document.createElement('link');
      link.rel = 'alternate';
      link.hreflang = slugToHreflang[slug];
      link.href = withSlash(SITE_ORIGIN + buildLocalePath(slug, subpath));
      link.setAttribute('data-hreflang-meta', '');
      head.appendChild(link);
    }

    // x-default → english variant
    const xdefault = document.createElement('link');
    xdefault.rel = 'alternate';
    xdefault.hreflang = 'x-default';
    xdefault.href = withSlash(SITE_ORIGIN + buildLocalePath(DEFAULT_SLUG, subpath));
    xdefault.setAttribute('data-hreflang-meta', '');
    head.appendChild(xdefault);

    return () => {
      document
        .querySelectorAll('link[data-hreflang-meta]')
        .forEach((el) => el.remove());
    };
  }, [currentSlug, subpath]);

  return null;
}
